import { computed, makeObservable, observable, runInAction } from 'mobx';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { getLineGroupingName } from 'src/domains/sportsbook/webview/components/eventMarkets/groupMarkets/getLineGroupingName';

/** How long the next line market must stay in the feed before we swap / add a slot. */
const SWAP_STABLE_MS = 3500;
/** Drop a suspended slot if no replacement appears. */
const EMPTY_REMOVE_MS = 12000;
/** Ignore swap-back to the previous line/id for this long (anti ping-pong). */
const PING_PONG_BLOCK_MS = 8000;
/** Stagger simultaneous group swaps so the page does not update in one frame. */
const SWAP_STAGGER_MS = 150;
const SWAP_STAGGER_SLOTS = 5;
/** Fallback when options omit maxSlotsPerGroup — prefer per-sport config at call site. */
const DEFAULT_MAX_SLOTS_PER_GROUP = 1;

const hasKeyLine = (market: EventMarketItemType): boolean => market.keyLine !== null && market.keyLine !== undefined;

const rawMarketId = (market: EventMarketItemType): number => market.id2.toOldId();

type LineSlot = {
    market: EventMarketItemType;
    suspended: boolean;
    pendingCandidate: EventMarketItemType | null;
    swapTimer: ReturnType<typeof setTimeout> | null;
    emptyTimer: ReturnType<typeof setTimeout> | null;
    suspendedAt: number | null;
    pendingSince: number | null;
    /** Market we swapped away from — used to block immediate bounce-back. */
    previousMarketId: number | null;
    previousKeyLine: number | null;
    previousSwappedAt: number | null;
};

type PendingAdd = {
    market: EventMarketItemType;
    timer: ReturnType<typeof setTimeout> | null;
    since: number;
};

/** Sticky hold for markets without keyLine (match winner, game markets, etc.). */
type NonLineSlot = {
    market: EventMarketItemType;
    suspended: boolean;
    emptyTimer: ReturnType<typeof setTimeout> | null;
    suspendedAt: number | null;
};

const clearSlotTimers = (slot: LineSlot): void => {
    if (slot.swapTimer !== null) {
        clearTimeout(slot.swapTimer);
        slot.swapTimer = null;
    }
    if (slot.emptyTimer !== null) {
        clearTimeout(slot.emptyTimer);
        slot.emptyTimer = null;
    }
};

const clearNonLineSlotTimer = (slot: NonLineSlot): void => {
    if (slot.emptyTimer !== null) {
        clearTimeout(slot.emptyTimer);
        slot.emptyTimer = null;
    }
};

const clearPendingAddTimer = (pending: PendingAdd): void => {
    if (pending.timer !== null) {
        clearTimeout(pending.timer);
        pending.timer = null;
    }
};

const createNonLineSlot = (market: EventMarketItemType): NonLineSlot => ({
    market,
    suspended: false,
    emptyTimer: null,
    suspendedAt: null,
});

const sortByKeyLineThenId = (markets: Array<EventMarketItemType>): Array<EventMarketItemType> =>
    [...markets].sort((a, b) => {
        const aLine = a.keyLine ?? 0;
        const bLine = b.keyLine ?? 0;
        if (aLine !== bLine) {
            return aLine - bLine;
        }
        return rawMarketId(b) - rawMarketId(a);
    });

const pickReplacement = (
    candidates: Array<EventMarketItemType>,
    previous: EventMarketItemType | null,
    blockedId: number | null,
    blockedKeyLine: number | null,
    blockActive: boolean
): EventMarketItemType | null => {
    if (candidates.length === 0) {
        return null;
    }

    const filtered = blockActive
        ? candidates.filter((candidate) => {
              const id = rawMarketId(candidate);
              const keyLine = candidate.keyLine ?? null;
              if (blockedId !== null && id === blockedId) {
                  return false;
              }
              if (blockedKeyLine !== null && keyLine !== null && keyLine === blockedKeyLine) {
                  return false;
              }
              return true;
          })
        : candidates;

    const pool = filtered.length > 0 ? filtered : [];
    if (pool.length === 0) {
        return null;
    }

    if (previous?.keyLine !== null && previous?.keyLine !== undefined) {
        const prevLine = previous.keyLine;
        const sorted = [...pool].sort((a, b) => {
            const aLine = a.keyLine ?? 0;
            const bLine = b.keyLine ?? 0;
            const aDist = Math.abs(aLine - prevLine);
            const bDist = Math.abs(bLine - prevLine);
            if (aDist !== bDist) {
                return aDist - bDist;
            }
            return rawMarketId(b) - rawMarketId(a);
        });
        return sorted[0] ?? null;
    }

    return [...pool].sort((a, b) => rawMarketId(b) - rawMarketId(a))[0] ?? null;
};

const createSlot = (market: EventMarketItemType): LineSlot => ({
    market,
    suspended: false,
    pendingCandidate: null,
    swapTimer: null,
    emptyTimer: null,
    suspendedAt: null,
    pendingSince: null,
    previousMarketId: null,
    previousKeyLine: null,
    previousSwappedAt: null,
});

export type LineMarketSlotCoalesceOptions = {
    swapStableMs?: number;
    emptyRemoveMs?: number;
    pingPongBlockMs?: number;
    maxSlotsPerGroup?: number;
};

export class LineMarketSlotCoalesceState {
    @observable private version: number = 0;
    @observable.ref private lastLive: Array<EventMarketItemType> = [];

    private readonly slotsByGroup = new Map<string, Array<LineSlot>>();
    private readonly pendingAddsByGroup = new Map<string, Array<PendingAdd>>();
    private readonly nonLineSlots = new Map<number, NonLineSlot>();
    private readonly pendingNonLineAdds = new Map<number, PendingAdd>();
    private swapStaggerIndex = 0;
    private readonly swapStableMs: number;
    private readonly emptyRemoveMs: number;
    private readonly pingPongBlockMs: number;
    private readonly maxSlotsPerGroup: number;

    public constructor(options: LineMarketSlotCoalesceOptions = {}) {
        this.swapStableMs = options.swapStableMs ?? SWAP_STABLE_MS;
        this.emptyRemoveMs = options.emptyRemoveMs ?? EMPTY_REMOVE_MS;
        this.pingPongBlockMs = options.pingPongBlockMs ?? PING_PONG_BLOCK_MS;
        this.maxSlotsPerGroup = options.maxSlotsPerGroup ?? DEFAULT_MAX_SLOTS_PER_GROUP;
        makeObservable(this);
    }

    private isPingPongBlocked = (slot: LineSlot): boolean => {
        if (slot.previousSwappedAt === null) {
            return false;
        }
        return Date.now() - slot.previousSwappedAt < this.pingPongBlockMs;
    };

    private nextStaggerDelay = (): number => {
        const offset = (this.swapStaggerIndex % SWAP_STAGGER_SLOTS) * SWAP_STAGGER_MS;
        this.swapStaggerIndex += 1;
        return offset;
    };

    private getGroupSlots = (groupKey: string): Array<LineSlot> => {
        const existing = this.slotsByGroup.get(groupKey);
        if (existing !== undefined) {
            return existing;
        }
        const created: Array<LineSlot> = [];
        this.slotsByGroup.set(groupKey, created);
        return created;
    };

    private getPendingAdds = (groupKey: string): Array<PendingAdd> => {
        const existing = this.pendingAddsByGroup.get(groupKey);
        if (existing !== undefined) {
            return existing;
        }
        const created: Array<PendingAdd> = [];
        this.pendingAddsByGroup.set(groupKey, created);
        return created;
    };

    private claimedIds = (groupKey: string, slots: Array<LineSlot>): Set<number> => {
        const ids = new Set<number>();
        for (const slot of slots) {
            ids.add(rawMarketId(slot.market));
            if (slot.pendingCandidate !== null) {
                ids.add(rawMarketId(slot.pendingCandidate));
            }
        }
        for (const pending of this.getPendingAdds(groupKey)) {
            ids.add(rawMarketId(pending.market));
        }
        return ids;
    };

    private freeLive = (liveInGroup: Array<EventMarketItemType>, claimed: Set<number>): Array<EventMarketItemType> =>
        liveInGroup.filter((market) => claimed.has(rawMarketId(market)) === false);

    private scheduleSwap = (groupKey: string, slot: LineSlot): void => {
        if (slot.swapTimer !== null) {
            clearTimeout(slot.swapTimer);
            slot.swapTimer = null;
        }

        const delay = this.swapStableMs + this.nextStaggerDelay();
        slot.swapTimer = setTimeout(() => {
            const slots = this.slotsByGroup.get(groupKey);
            if (slots === undefined || slots.includes(slot) === false || slot.pendingCandidate === null) {
                return;
            }

            const fromId = rawMarketId(slot.market);
            const fromLine = slot.market.keyLine ?? null;
            const toMarket = slot.pendingCandidate;
            const toId = rawMarketId(toMarket);
            const toLine = toMarket.keyLine ?? null;

            // Final ping-pong guard at commit time.
            if (
                slot.previousSwappedAt !== null &&
                Date.now() - slot.previousSwappedAt < this.pingPongBlockMs &&
                ((slot.previousMarketId !== null && toId === slot.previousMarketId) ||
                    (slot.previousKeyLine !== null && toLine !== null && toLine === slot.previousKeyLine))
            ) {
                slot.pendingCandidate = null;
                slot.pendingSince = null;
                slot.swapTimer = null;
                runInAction(() => {
                    this.version += 1;
                });
                return;
            }

            slot.previousMarketId = fromId;
            slot.previousKeyLine = fromLine;
            slot.previousSwappedAt = Date.now();
            slot.market = toMarket;
            slot.pendingCandidate = null;
            slot.swapTimer = null;
            slot.suspended = false;
            slot.suspendedAt = null;
            slot.pendingSince = null;

            runInAction(() => {
                this.version += 1;
            });
        }, delay);
    };

    private schedulePendingAdd = (groupKey: string, market: EventMarketItemType): void => {
        const pendingAdds = this.getPendingAdds(groupKey);
        const marketId = rawMarketId(market);
        if (pendingAdds.some((pending) => rawMarketId(pending.market) === marketId)) {
            return;
        }

        const pending: PendingAdd = {
            market,
            timer: null,
            since: Date.now(),
        };

        const delay = this.swapStableMs + this.nextStaggerDelay();
        pending.timer = setTimeout(() => {
            const slots = this.getGroupSlots(groupKey);
            const pendings = this.getPendingAdds(groupKey);
            const index = pendings.indexOf(pending);
            if (index >= 0) {
                pendings.splice(index, 1);
            }

            if (slots.length >= this.maxSlotsPerGroup) {
                return;
            }

            const stillLive = this.lastLive.some(
                (liveMarket) => hasKeyLine(liveMarket) && rawMarketId(liveMarket) === marketId
            );
            if (stillLive === false) {
                return;
            }

            if (slots.some((slot) => rawMarketId(slot.market) === marketId)) {
                return;
            }

            slots.push(createSlot(pending.market));
            runInAction(() => {
                this.version += 1;
            });
        }, delay);

        pendingAdds.push(pending);
    };

    private clearPendingAddsNotIn = (groupKey: string, allowedIds: Set<number>): boolean => {
        const pendingAdds = this.getPendingAdds(groupKey);
        let changed = false;
        for (let i = pendingAdds.length - 1; i >= 0; i -= 1) {
            const pending = pendingAdds[i];
            if (pending === undefined) {
                continue;
            }
            if (allowedIds.has(rawMarketId(pending.market))) {
                continue;
            }
            clearPendingAddTimer(pending);
            pendingAdds.splice(i, 1);
            changed = true;
        }
        return changed;
    };

    private removeSlot = (groupKey: string, slot: LineSlot): void => {
        const slots = this.slotsByGroup.get(groupKey);
        if (slots === undefined) {
            return;
        }
        clearSlotTimers(slot);
        const index = slots.indexOf(slot);
        if (index >= 0) {
            slots.splice(index, 1);
        }
        if (slots.length === 0) {
            this.slotsByGroup.delete(groupKey);
        }
    };

    private syncGroup = (groupKey: string, liveInGroup: Array<EventMarketItemType>): boolean => {
        const slots = this.getGroupSlots(groupKey);
        let changed = false;

        if (slots.length === 0) {
            if (liveInGroup.length === 0) {
                this.slotsByGroup.delete(groupKey);
                this.clearPendingAddsNotIn(groupKey, new Set());
                return false;
            }

            const initial = sortByKeyLineThenId(liveInGroup).slice(0, this.maxSlotsPerGroup);
            for (const market of initial) {
                slots.push(createSlot(market));
            }
            this.clearPendingAddsNotIn(groupKey, new Set());
            return true;
        }

        const liveIds = new Set(liveInGroup.map(rawMarketId));

        for (const slot of slots) {
            const slotId = rawMarketId(slot.market);
            const liveCurrent = liveInGroup.find((market) => rawMarketId(market) === slotId);

            if (liveCurrent !== undefined) {
                slot.market = liveCurrent;
                if (slot.suspended || slot.pendingCandidate !== null) {
                    clearSlotTimers(slot);
                    slot.suspended = false;
                    slot.pendingCandidate = null;
                    slot.suspendedAt = null;
                    slot.pendingSince = null;
                    changed = true;
                }
                continue;
            }

            if (liveInGroup.length === 0) {
                if (slot.suspended === false) {
                    slot.suspended = true;
                    slot.suspendedAt = Date.now();
                    changed = true;
                }
                slot.pendingCandidate = null;
                slot.pendingSince = null;
                if (slot.swapTimer !== null) {
                    clearTimeout(slot.swapTimer);
                    slot.swapTimer = null;
                }
                if (slot.emptyTimer === null) {
                    slot.emptyTimer = setTimeout(() => {
                        this.removeSlot(groupKey, slot);
                        runInAction(() => {
                            this.version += 1;
                        });
                    }, this.emptyRemoveMs);
                }
                continue;
            }

            if (slot.suspended === false) {
                slot.suspended = true;
                slot.suspendedAt = Date.now();
                changed = true;
            }
            if (slot.emptyTimer !== null) {
                clearTimeout(slot.emptyTimer);
                slot.emptyTimer = null;
            }

            const claimed = this.claimedIds(groupKey, slots);
            // Allow this slot's own pending candidate to be re-picked.
            if (slot.pendingCandidate !== null) {
                claimed.delete(rawMarketId(slot.pendingCandidate));
            }
            claimed.delete(slotId);

            const blockActive = this.isPingPongBlocked(slot);
            const candidate = pickReplacement(
                this.freeLive(liveInGroup, claimed),
                slot.market,
                slot.previousMarketId,
                slot.previousKeyLine,
                blockActive
            );

            if (candidate === null) {
                if (slot.pendingCandidate !== null || slot.swapTimer !== null) {
                    if (slot.swapTimer !== null) {
                        clearTimeout(slot.swapTimer);
                        slot.swapTimer = null;
                    }
                    slot.pendingCandidate = null;
                    slot.pendingSince = null;
                    changed = true;
                }
                if (slot.emptyTimer === null) {
                    slot.emptyTimer = setTimeout(() => {
                        this.removeSlot(groupKey, slot);
                        runInAction(() => {
                            this.version += 1;
                        });
                    }, this.emptyRemoveMs);
                }
                continue;
            }

            const candidateId = rawMarketId(candidate);
            const pendingId = slot.pendingCandidate !== null ? rawMarketId(slot.pendingCandidate) : null;

            if (pendingId !== candidateId) {
                slot.pendingCandidate = candidate;
                slot.pendingSince = Date.now();
                this.scheduleSwap(groupKey, slot);
                changed = true;
            }
        }

        const claimedAfter = this.claimedIds(groupKey, slots);
        const free = this.freeLive(liveInGroup, claimedAfter);
        const room = this.maxSlotsPerGroup - slots.length - this.getPendingAdds(groupKey).length;

        if (room > 0 && free.length > 0) {
            for (const market of sortByKeyLineThenId(free).slice(0, room)) {
                this.schedulePendingAdd(groupKey, market);
                changed = true;
            }
        }

        if (this.clearPendingAddsNotIn(groupKey, liveIds)) {
            changed = true;
        }

        // Refresh pending add market refs while waiting.
        for (const pending of this.getPendingAdds(groupKey)) {
            const fresh = liveInGroup.find((market) => rawMarketId(market) === rawMarketId(pending.market));
            if (fresh !== undefined) {
                pending.market = fresh;
            }
        }

        if (slots.length === 0) {
            this.slotsByGroup.delete(groupKey);
        }

        return changed;
    };

    private schedulePendingNonLineAdd = (market: EventMarketItemType): void => {
        const marketId = rawMarketId(market);
        if (this.pendingNonLineAdds.has(marketId) || this.nonLineSlots.has(marketId)) {
            return;
        }

        const pending: PendingAdd = {
            market,
            timer: null,
            since: Date.now(),
        };

        const delay = this.swapStableMs + this.nextStaggerDelay();
        pending.timer = setTimeout(() => {
            this.pendingNonLineAdds.delete(marketId);

            const stillLive = this.lastLive.some(
                (liveMarket) => hasKeyLine(liveMarket) === false && rawMarketId(liveMarket) === marketId
            );
            if (stillLive === false || this.nonLineSlots.has(marketId)) {
                return;
            }

            this.nonLineSlots.set(marketId, createNonLineSlot(pending.market));
            runInAction(() => {
                this.version += 1;
            });
        }, delay);

        this.pendingNonLineAdds.set(marketId, pending);
    };

    private removeNonLineSlot = (marketId: number): void => {
        const slot = this.nonLineSlots.get(marketId);
        if (slot === undefined) {
            return;
        }
        clearNonLineSlotTimer(slot);
        this.nonLineSlots.delete(marketId);
    };

    private syncNonLine = (liveNonLine: Array<EventMarketItemType>): boolean => {
        let changed = false;
        const liveById = new Map(liveNonLine.map((market) => [rawMarketId(market), market]));

        if (this.nonLineSlots.size === 0 && this.pendingNonLineAdds.size === 0) {
            if (liveNonLine.length === 0) {
                return false;
            }
            for (const market of liveNonLine) {
                this.nonLineSlots.set(rawMarketId(market), createNonLineSlot(market));
            }
            return true;
        }

        for (const [marketId, slot] of this.nonLineSlots) {
            const liveCurrent = liveById.get(marketId);
            if (liveCurrent !== undefined) {
                slot.market = liveCurrent;
                if (slot.suspended) {
                    clearNonLineSlotTimer(slot);
                    slot.suspended = false;
                    slot.suspendedAt = null;
                    changed = true;
                }
                continue;
            }

            if (slot.suspended === false) {
                slot.suspended = true;
                slot.suspendedAt = Date.now();
                changed = true;
            }
            if (slot.emptyTimer === null) {
                slot.emptyTimer = setTimeout(() => {
                    this.removeNonLineSlot(marketId);
                    runInAction(() => {
                        this.version += 1;
                    });
                }, this.emptyRemoveMs);
            }
        }

        for (const market of liveNonLine) {
            const marketId = rawMarketId(market);
            if (this.nonLineSlots.has(marketId)) {
                continue;
            }
            const pending = this.pendingNonLineAdds.get(marketId);
            if (pending !== undefined) {
                pending.market = market;
                continue;
            }
            this.schedulePendingNonLineAdd(market);
            changed = true;
        }

        for (const [marketId, pending] of this.pendingNonLineAdds) {
            if (liveById.has(marketId)) {
                continue;
            }
            clearPendingAddTimer(pending);
            this.pendingNonLineAdds.delete(marketId);
            changed = true;
        }

        return changed;
    };

    public sync = (live: Array<EventMarketItemType>): void => {
        const liveByGroup = new Map<string, Array<EventMarketItemType>>();
        const liveNonLine: Array<EventMarketItemType> = [];

        for (const market of live) {
            if (!hasKeyLine(market)) {
                liveNonLine.push(market);
                continue;
            }
            const groupKey = getLineGroupingName(market.name, market.keyLine);
            const list = liveByGroup.get(groupKey) ?? [];
            list.push(market);
            liveByGroup.set(groupKey, list);
        }

        const groupKeys = new Set<string>([
            ...this.slotsByGroup.keys(),
            ...this.pendingAddsByGroup.keys(),
            ...liveByGroup.keys(),
        ]);
        let changed = false;

        for (const groupKey of groupKeys) {
            if (this.syncGroup(groupKey, liveByGroup.get(groupKey) ?? [])) {
                changed = true;
            }
        }

        if (this.syncNonLine(liveNonLine)) {
            changed = true;
        }

        runInAction(() => {
            this.lastLive = live;
            if (changed) {
                this.version += 1;
            }
        });
    };

    @computed public get viewMarkets(): Array<EventMarketItemType> {
        void this.version;

        const nonLine = [...this.nonLineSlots.values()].map((slot) => slot.market);
        const lineSlots: Array<EventMarketItemType> = [];
        for (const slots of this.slotsByGroup.values()) {
            for (const slot of slots) {
                lineSlots.push(slot.market);
            }
        }

        return [...nonLine, ...lineSlots];
    }

    @computed public get suspendedMarketIds(): ReadonlySet<number> {
        void this.version;
        const ids = new Set<number>();
        for (const slots of this.slotsByGroup.values()) {
            for (const slot of slots) {
                if (slot.suspended) {
                    ids.add(rawMarketId(slot.market));
                }
            }
        }
        for (const [marketId, slot] of this.nonLineSlots) {
            if (slot.suspended) {
                ids.add(marketId);
            }
        }
        return ids;
    }

    public dispose = (): void => {
        for (const slots of this.slotsByGroup.values()) {
            for (const slot of slots) {
                clearSlotTimers(slot);
            }
        }
        for (const pendingAdds of this.pendingAddsByGroup.values()) {
            for (const pending of pendingAdds) {
                clearPendingAddTimer(pending);
            }
        }
        for (const slot of this.nonLineSlots.values()) {
            clearNonLineSlotTimer(slot);
        }
        for (const pending of this.pendingNonLineAdds.values()) {
            clearPendingAddTimer(pending);
        }
        this.slotsByGroup.clear();
        this.pendingAddsByGroup.clear();
        this.nonLineSlots.clear();
        this.pendingNonLineAdds.clear();
        runInAction(() => {
            this.lastLive = [];
            this.version += 1;
        });
    };
}
